import React from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Popper } from '@mui/material'

// Custom Popper component with high z-index to appear above modals
// Using z-index 10000 to be higher than Dialog's 9999
const CustomPopper = React.forwardRef((props, ref) => {
	return (
		<Popper
			{...props}
			ref={ref}
			style={{ ...props.style, zIndex: 10000 }}
			sx={{ zIndex: 10000 }}
			disablePortal={false}
		/>
	)
})

CustomPopper.displayName = 'CustomPopper'

export default function DatePicker({ name, label, value, onChange, disabled = false, error, size }) {
	const convertToDefEventPara = (name, value) => ({
		target: {
			name,
			value,
		},
	})

	// Convert string date values to Date objects for MUI DatePicker
	// MUI DatePicker expects a Date object or null/undefined
	const parseDateValue = value => {
		if (value === null || value === undefined) {
			return null
		}
		// If it's already a Date object, return it
		if (value instanceof Date) {
			return value
		}
		// If it's a string, parse it to a Date object
		if (typeof value === 'string') {
			const parsedDate = new Date(value)
			// Check if the date is valid
			return isNaN(parsedDate.getTime()) ? null : parsedDate
		}
		// Fallback: try to convert to Date
		try {
			const parsedDate = new Date(value)
			return isNaN(parsedDate.getTime()) ? null : parsedDate
		} catch {
			return null
		}
	}

	const dateValue = parseDateValue(value)

	// Shrink label when there's a value to prevent overlap with input data
	const hasValue = dateValue !== null && dateValue !== undefined

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<MuiDatePicker
				label={label}
				value={dateValue}
				disabled={disabled}
				onChange={date => onChange(convertToDefEventPara(name, date))}
				slots={{
					popper: CustomPopper,
				}}
				slotProps={{
					textField: {
						name,
						variant: 'outlined',
						size: size,
						error: !!error,
						helperText: error,
						InputLabelProps: {
							shrink: hasValue, // Shrink when there's a value; MUI handles focus automatically
						},
					},
					popper: {
						modifiers: [
							{
								name: 'preventOverflow',
								enabled: true,
							},
						],
					},
				}}
				format="MM/dd/yy"
			/>
		</LocalizationProvider>
	)
}
